import React, { useEffect, useMemo, useState } from 'react';
import Button3D from '../ui/Button3D';
import Modal3D from '../ui/Modal3D';
import ResetTestContactDialog from './ResetTestContactDialog';
import { supabaseClient } from '../../config/supabase';
import { canResetTestContact } from '../../lib/testContactReset';
import { describeTestList, parseTestEntries } from '../../lib/businessSchedule';
import { D2_TEXTAREA } from '../settings/signatures/d2Fields';

// BL-177 Step 2m: choosing Test asks who the AI may talk to. Several phone numbers and/or emails.
// While the status is Test the AI replies ONLY to these, on every channel; everyone else gets no AI reply at all.
export default function TestContactsDialog({ contacts = [], onSave, onClose, busy = false, client = supabaseClient }) {
    const [kept, setKept] = useState(contacts);
    // BL-177 Step 2o: a saved entry can be reset (wiped back to a first-time visitor) by a company admin. The database decides who may.
    const [resettable, setResettable] = useState({});
    const [resetting, setResetting] = useState(null);
    useEffect(() => {
        let live = true;
        Promise.all(contacts.map(async (entry) => [entry.value, await canResetTestContact(client, entry.value)])).then((pairs) => {
            if (live) setResettable(Object.fromEntries(pairs));
        });
        return () => { live = false; };
    }, [contacts, client]);
    const [text, setText] = useState('');
    const [problems, setProblems] = useState([]);
    const [error, setError] = useState('');

    const typed = useMemo(() => parseTestEntries(text), [text]);
    const keptValues = new Set(kept.map((entry) => entry.value));
    const added = typed.entries.filter((entry) => !keptValues.has(entry.value));

    async function save() {
        if (typed.problems.length) { setProblems(typed.problems); return; }
        const next = [...kept, ...added];
        if (!next.length) { setProblems(['Add at least one phone number or email to test with.']); return; }
        setProblems([]);
        setError('');
        try {
            await onSave(next);
        } catch (saveError) {
            setError(saveError?.message || 'The test list could not be saved.');
        }
    }

    // one popup at a time: the confirm step takes the place of this one, and Close / Cancel brings the list back
    if (resetting) {
        return <ResetTestContactDialog address={resetting} client={client} onClose={() => setResetting(null)} />;
    }

    return (
        <Modal3D
            className="ia-settings-popup"
            title="Who can the AI talk to while testing?"
            subtitle="Only these phones and emails get an AI reply."
            stripe="gold"
            portal
            testId="ai-test-dialog"
            dismissible={!busy}
            onClose={onClose}
            actions={(
                <>
                    <Button3D type="button" onClick={onClose} disabled={busy}>Cancel</Button3D>
                    <Button3D variant="go" onClick={save} disabled={busy}>Start testing</Button3D>
                </>
            )}
        >
            <p className="text-sm">
                In Test, the assistant answers <strong>only</strong> these phone numbers and emails, on text, WhatsApp and email.
                Everyone else gets no AI reply at all. Bookings it makes are marked TEST: no driver, no bill, no client notices.
            </p>
            {kept.length > 0 && (
                <ul aria-label="Test list" className="space-y-1">
                    {kept.map((entry) => (
                        <li key={entry.value} className="flex items-center justify-between gap-2 rounded-lg border border-[#33415E] bg-[#0A101C] px-3 py-1.5">
                            <span className="font-semibold">{entry.value}</span>
                            <span className="flex items-center gap-3">
                                {resettable[entry.value] && contacts.some((saved) => saved.value === entry.value) && (
                                    <button type="button" className="text-xs font-bold text-[#FCD34D] underline" data-testid="ai-test-reset"
                                        aria-label={`Reset ${entry.value}`} disabled={busy} onClick={() => setResetting(entry.value)}>Reset</button>
                                )}
                                <button type="button" className="text-xs font-bold text-[#5EEAD4] underline"
                                    aria-label={`Remove ${entry.value}`} onClick={() => setKept(kept.filter((other) => other.value !== entry.value))}>Remove</button>
                            </span>
                        </li>
                    ))}
                </ul>
            )}
            <label className="block">
                <span className="mb-1 block font-bold">Add phone numbers or emails</span>
                <textarea
                    value={text}
                    onChange={(event) => { setText(event.target.value); setProblems([]); }}
                    rows={3}
                    className={D2_TEXTAREA}
                    placeholder="(404) 555-0100, name@example.com"
                    aria-describedby="ai-test-help"
                />
                <span id="ai-test-help" className="mt-1 block text-xs text-[#C7D2E8]">One per line, or separated by commas. Add as many as you need.</span>
            </label>
            {added.length > 0 && <p className="text-xs text-[#C7D2E8]" data-testid="ai-test-added">Adding: {describeTestList(added)}</p>}
            {problems.length > 0 && (
                <ul role="alert" className="space-y-1 text-xs font-bold text-[#FCA5A5]">
                    {problems.map((problem) => <li key={problem}>{problem}</li>)}
                </ul>
            )}
            {error && <p role="alert" className="text-xs font-bold text-[#FCA5A5]">{error}</p>}
        </Modal3D>
    );
}
